import api from './api';
import { API_ROUTES } from '../config/api';

export interface ArticlePromotionImpact {
  id: number;
  promotion: number;
  promotion_id?: number;
  promotion_name?: string;
  article_id: string;
  impact_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface ArticlePromotionPair {
  id: string;
  promotion_id: number;
  promotion_name: string;
  promotion_start_date: string;
  promotion_end_date: string;
  promotion_status: 'en_cours' | 'termine' | 'en_attente' | 'annule';
  article_id: string;
  article: Record<string, any>;
  impact_percentage: number;
  predicted_sales_sum: number;
  additional_sales: number;
  historical_sales_sum: number;
}

export interface CreateImpactData {
  article_id: string;
  impact_percentage: number;
}

export interface UpdateImpactData {
  impact_percentage: number;
}

class ArticlePromotionImpactService {
  
  /**
   * Get all article-promotion pairs with computed metrics
   */
  async getArticlePromotionPairs(
    databaseId: number | string,
    promotionId?: number
  ): Promise<ArticlePromotionPair[]> {
    try {
      const url = API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.ARTICLE_PROMOTION_PAIRS(databaseId);
      const params = promotionId ? { promotion_id: promotionId } : {};
      const response = await api.get(url, { params });
      return response.data.pairs || [];
    } catch (error) {
      console.error(`Error fetching article-promotion pairs for database ID ${databaseId}:`, error);
      throw error;
    }
  }

  /**
   * Get all impacts for a specific promotion
   */
  async getImpacts(
    databaseId: number | string,
    promotionId: number
  ): Promise<ArticlePromotionImpact[]> {
    try {
      const response = await api.get(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.LIST(databaseId, promotionId)
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching impacts for promotion ${promotionId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific impact by ID
   */
  async getImpact(
    databaseId: number | string,
    promotionId: number,
    impactId: number
  ): Promise<ArticlePromotionImpact> {
    try {
      const response = await api.get(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.DETAIL(databaseId, promotionId, impactId)
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching impact ${impactId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new impact
   */
  async createImpact(
    databaseId: number | string,
    promotionId: number,
    data: CreateImpactData
  ): Promise<ArticlePromotionImpact> {
    try {
      const response = await api.post(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.LIST(databaseId, promotionId),
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating impact for promotion ${promotionId}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing impact
   */
  async updateImpact(
    databaseId: number | string,
    promotionId: number,
    impactId: number,
    data: UpdateImpactData
  ): Promise<ArticlePromotionImpact> {
    try {
      const response = await api.put(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.DETAIL(databaseId, promotionId, impactId),
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating impact ${impactId}:`, error);
      throw error;
    }
  }

  /**
   * Partially update an impact (PATCH)
   */
  async patchImpact(
    databaseId: number | string,
    promotionId: number,
    impactId: number,
    data: Partial<UpdateImpactData>
  ): Promise<ArticlePromotionImpact> {
    try {
      const response = await api.patch(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.DETAIL(databaseId, promotionId, impactId),
        data
      );
      return response.data;
    } catch (error) {
      console.error(`Error patching impact ${impactId}:`, error);
      throw error;
    }
  }

  /**
   * Delete an impact
   */
  async deleteImpact(
    databaseId: number | string,
    promotionId: number,
    impactId: number
  ): Promise<void> {
    try {
      await api.delete(
        API_ROUTES.PROMOTIONS.BASE + API_ROUTES.PROMOTIONS.IMPACTS.DETAIL(databaseId, promotionId, impactId)
      );
    } catch (error) {
      console.error(`Error deleting impact ${impactId}:`, error);
      throw error;
    }
  }

  /**
   * Create or update impact for an article-promotion pair
   * This is a convenience method that handles both create and update
   */
  async upsertImpact(
    databaseId: number | string,
    promotionId: number,
    articleId: string,
    impactPercentage: number
  ): Promise<ArticlePromotionImpact> {
    try {
      // First, try to find existing impact
      const impacts = await this.getImpacts(databaseId, promotionId);
      const existingImpact = impacts.find(impact => impact.article_id === articleId);

      if (existingImpact) {
        // Update existing impact
        return await this.updateImpact(
          databaseId,
          promotionId,
          existingImpact.id,
          { impact_percentage: impactPercentage }
        );
      } else {
        // Create new impact
        return await this.createImpact(databaseId, promotionId, {
          article_id: articleId,
          impact_percentage: impactPercentage
        });
      }
    } catch (error) {
      console.error(`Error upserting impact for article ${articleId} in promotion ${promotionId}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
const articlePromotionImpactService = new ArticlePromotionImpactService();
export default articlePromotionImpactService;


